import { BasicFile, CheckboxAPI, FetchAPI, FieldAPI, FileUploaderAPI, FormAPI, InputAPI, MemoryFileHandler, ModalService, PhoneNumberAPI, PredefinedFile, RadioGroupAPI, RecaptchaAPI, ThemeTonalRange, ToastNotificationAPI, ToastService, WizardAPI, WizardStepAPI } from '@philips-internal/oswald-ui-components';
import { Value } from '@philips-internal/oswald-ui-core';
import Controller, { OnAfterFetch, OnBlur, OnChange, OnClear, OnClick, OnMount, OnNavigate, OnSubmit, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { AbortableNavigateEvent, ChangeEvent, ClearEvent, FocusEvent, MountEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService, LanguageService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { Inject } from '@odinjs/odin';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { cast, clone } from '../../utils/cast-helpers';
import BaseController from '../BaseController';
import AccountCreationBlackboard from './account-creation-blackboard';
import { Validation, Validator } from '@philips-internal/oswald-ui-core/lib/services/model-service';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import { getCatpchaPublicKey } from '../../utils/captcha-utils';
import { LocaleTypes } from '../../constants/locale-types';
import { Country, InputType } from '@philips-internal/oswald-ui-constants';
import { OnClickAviso, OnClickTermo, OnClickTermoConsent } from '../../app/events';
import ConfigurationService from '../../services/configuration-service';
import { TermsOfUseType } from '../../constants/terms-of-use-type';
import PatientModalService from '../../services/patient-modal-service';
import { AccountCreationStepsAliases } from '../../codegen';
import { WizardUtils } from '../../utils/wizard-utils';
import { UsernameValidatorUtils } from '../../utils/username-validator-utils';
import { CRYPTOPW_MAX_LENGTH, USERNAME_MIN_LENGTH_VALIDATION } from '../../constants/application-constants';
import { AccountCreationBoliviaHelper } from './account-creation-bolivia-helper';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import { HttpServiceError, PatientHttpService } from '../../services/patient-http-service';
import { PredefinedValidators } from '../../utils/predefined-validators';
import { AccountCreationArgentinaHelper } from './account-creation-argentina-helper';
import { DocumentTypeDefinitions } from '../../utils/document-type';
import { DocumentType } from '../../constants/document-type';
import { FileProviderData } from '../../constants/file-provider-enum';
import { PseudonymCustomMenssage } from '../pseudonym-appointments/pseudonym-interfaces';
import { YES } from '../pseudonym-appointments/pseudonym-types';
import { AccountCreationSettings, DocumentUploadRuleResponse, LoginTypeParamResponse, RecoveryTypeProps, ValidationErrors } from '../../interfaces/account-creation';
import { StepNames, UPLOAD_RULE_STAGE } from '../../constants/account-creation-constants';
import { DEFAULT_TIMEOUT } from '../../utils/toast-notification-utils';

@Controller({ component: 'layout-root', fragment: 'account-creation-steps' })
export default class AccountCreationModalController extends BaseController {

  @Inject('AccountCreationBlackboard')
    accountCreationBlackboard!: AccountCreationBlackboard;

  @Inject
    fetchDocumentType!: FetchAPI;

  @Inject('form-account-information')
    formAccountInformation!: FormAPI;

  @Inject('field-email')
    emailField!: FieldAPI;

  @Inject(AccountCreationStepsAliases.FIELD_DOCUMENT_TYPE)
    fieldDocumentType!: FieldAPI;

  @Inject(AccountCreationStepsAliases.FIELD_DOCUMENT_VALUE)
    fieldDocumentValue!: FieldAPI;

  @Inject(AccountCreationStepsAliases.INPUT_DOCUMENT_VALUE)
    inputDocumentValue!: InputAPI;

  @Inject('field-user-inactive')
    fieldUserInactive!: FieldAPI;

  @Inject('input-email')
    inputEmail!: InputAPI;

  @Inject(AccountCreationStepsAliases.INPUT_CONFIRM_EMAIL)
    inputConfirmEmail!: InputAPI;

  @Inject('field-confirm-email')
    fieldConfirmEmail!: FieldAPI;

  @Inject('input-confirm-password')
    inputConfirmPassword!: InputAPI;

  @Inject('field-confirm-password')
    fieldConfirmPassword!: FieldAPI;

  @Inject('form-personal-identification')
    formPersonalIdentification!: FormAPI;

  @Inject('form-personal-information')
    formPersonalInformation!: FormAPI;

  @Inject('field-verifying-digit')
    fieldVerifyingDigit!: FieldAPI;

  @Inject('field-brasil-cpf')
    fieldBrasilCpf!: FieldAPI;

  @Inject('field-brasil-identidade')
    fieldBrasilIdentidade!: FieldAPI;

  @Inject('field-foreign-passport')
    fieldForeignPassport!: FieldAPI;

  @Inject('field-foreign-id')
    fieldForeignId!: FieldAPI;

  @Inject('field-foreign-nationality')
    fieldForeignNationality!: FieldAPI;

  @Inject('field-medicare')
    fieldMedicare!: FieldAPI;

  @Inject('field-departments')
    fieldDepartments!: FieldAPI;

  @Inject(AccountCreationStepsAliases.FIELD_GIVEN_NAME_ACCOUNT_CREATION_STEPS)
    fieldMainPersonName1Api!: FieldAPI;

  @Inject(AccountCreationStepsAliases.FIELD_COMPONENT_NAME_1_ACCOUNT_CREATION_STEPS)
    fieldMainPersonName2Api!: FieldAPI;

  @Inject(AccountCreationStepsAliases.FIELD_COMPONENT_NAME_2_ACCOUNT_CREATION_STEPS)
    fieldMainPersonName3Api!: FieldAPI;

  @Inject(AccountCreationStepsAliases.FIELD_COMPONENT_NAME_3_ACCOUNT_CREATION_STEPS)
    fieldMainPersonName4Api!: FieldAPI;

  @Inject(AccountCreationStepsAliases.FIELD_FAMILY_NAME_ACCOUNT_CREATION_STEPS)
    fieldMainPersonName5Api!: FieldAPI;

  @Inject('field-fullname')
    fieldFullname!: FieldAPI;

  @Inject('input-phone')
    inputPhone!: PhoneNumberAPI;

  @Inject('checklist-use-foreing-ids')
    checkboxUseForeingIds!: CheckboxAPI;

  @Inject('field-view-medicare')
    fieldViewMedicare!: FieldAPI;

  @Inject('field-view-brasil-identidade')
    fieldViewBrasilIdentidade!: FieldAPI;

  @Inject('field-view-brasil-cpf')
    fieldViewBrasilCpf!: FieldAPI;

  @Inject('field-view-departments')
    fieldViewDepartments!: FieldAPI;

  @Inject('field-view-verifying-digit')
    fieldViewVerifyingDigit!: FieldAPI;

  @Inject('field-view-foreign-nationality')
    fieldViewForeignNationality!: FieldAPI;

  @Inject('field-view-foreign-id')
    fieldViewForeignId!: FieldAPI;

  @Inject('field-view-foreign-passport')
    fieldViewForeignPassport!: FieldAPI;

  @Inject(AccountCreationStepsAliases.FIELD_VIEW_GIVEN_NAME_ACCOUNT_CREATION_STEPS)
    fieldViewMainPersonName1Api!: FieldAPI;

  @Inject(AccountCreationStepsAliases.FIELD_VIEW_COMPONENT_NAME_1_ACCOUNT_CREATION_STEPS)
    fieldViewMainPersonName2Api!: FieldAPI;

  @Inject(AccountCreationStepsAliases.FIELD_VIEW_COMPONENT_NAME_2_ACCOUNT_CREATION_STEPS)
    fieldViewMainPersonName3Api!: FieldAPI;

  @Inject(AccountCreationStepsAliases.FIELD_VIEW_COMPONENT_NAME_3_ACCOUNT_CREATION_STEPS)
    fieldViewMainPersonName4Api!: FieldAPI;

  @Inject(AccountCreationStepsAliases.FIELD_VIEW_FAMILY_NAME_ACCOUNT_CREATION_STEPS)
    fieldViewMainPersonName5Api!: FieldAPI;

  @Inject('field-view-fullname')
    fieldViewFullnameApi!: FieldAPI;

  stepsAccountCreation!: WizardAPI;

  @Inject('field-use-foreing-ids')
    fieldUseForeingIds!: FieldAPI;

  @Inject('field-sms-email')
    fieldValidationType!: FieldAPI;

  @Inject('field-dob')
    fieldBirthDate!: FieldAPI;

  @Inject(AccountCreationStepsAliases.FIELD_USERNAME)
    fieldUsernameAPI!: FieldAPI;

  @Inject('recaptcha-field')
    captchFieldApi!: FieldAPI;

  @Inject(AccountCreationStepsAliases.INPUT_USERNAME)
    inputUsernameAPI!: InputAPI;

  @Inject('field-password')
    fieldPasswordAPI!: FieldAPI;

  @Inject(AccountCreationStepsAliases.FIELD_GENDER)
    fieldGenderAPI!: FieldAPI;

  @Inject(AccountCreationStepsAliases.FIELD_VIEW_GENDER)
    fieldViewGenderAPI!: FieldAPI;

  @Inject(AccountCreationStepsAliases.TOAST_NOTIFICATION_UPLOAD_DOCUMENTS)
    notificationUploaderDocumentsAPI!: ToastNotificationAPI;

  @Inject(AccountCreationStepsAliases.FILE_UPLOADER_DOCUMENTS)
    fileUploaderDocumentsAPI!: FileUploaderAPI;

  @Inject(AccountCreationStepsAliases.FIELD_UPLOADER_DOCUMENTS)
    fieldUploaderDocumentsAPI!: FieldAPI;

  @Inject(AccountCreationStepsAliases.WIZARD_STEP_UPLOAD_DOCUMENTS)
    wizardStepUploadDocumentsAPI!: WizardStepAPI;

  @Inject(AccountCreationStepsAliases.FIELD_AGREE_TERMS)
    fieldAgreeTermsAPI!: FieldAPI;

  allForms: FormAPI[] = [];
  email!: string;
  confirmEmail!: string;
  password!: string;
  confirmPassword!: string;

  private uploadedFiles!: BasicFile[];
  private useForeignIds = false;
  private language = LanguageService.getCurrentLanguage();
  private backupOfValidatorsStep01: Record<string, Value> = {};
  private formPersonalIdentificationFields: FieldAPI[] = [];
  private emailChangeTimeout: null | ReturnType<typeof setTimeout> = null;

  @OnMount(AccountCreationStepsAliases.FILE_UPLOADER_DOCUMENTS)
  handlFileUploaderMount(event: MountEvent): void {
    const fileUploaderAPI = event.target as FileUploaderAPI;
    fileUploaderAPI.setFileHandlerProviderName(FileProviderData.fileProviderMemory);
  }

  @OnChange(AccountCreationStepsAliases.FILE_UPLOADER_DOCUMENTS)
  handleFileUploaderChange(event: ChangeEvent<BasicFile[]>): void {
    this.uploadedFiles = event.value;
  }

  @OnMount(AccountCreationStepsAliases.WIZARD_STEP_UPLOAD_DOCUMENTS)
  handleWizardStepUploadDocuments(): void {
    HttpService.get(APPLICATION_ENDPOINTS.DOCUMENT_UPLOAD_RULE, { stage: UPLOAD_RULE_STAGE.ACCOUNT_CREATION }).then(response => {
      const documentUploadRule = response.body as DocumentUploadRuleResponse[];

      if (documentUploadRule.length === 0) {
        this.stepsAccountCreation.setHiddenSteps([ StepNames.UploadDocuments ]);
        return;
      }

      const predefinedFiles = this.getPredefinedFile(documentUploadRule);
      this.fileUploaderDocumentsAPI.setPredefinedFiles(predefinedFiles);

      HttpService.get(APPLICATION_ENDPOINTS.GET_WSUITE_CUSTOM_MESSAGE_UPLOAD_CREATION_ACCOUNT, {}).then(customMessageResponse => {
        const customMessage = customMessageResponse.body as PseudonymCustomMenssage;
        const expression = customMessage?.message || MessageService.translate('patient.default.upload.document.message');
        this.notificationUploaderDocumentsAPI.setMessage(expression);
      })
    });
  }

  private getPredefinedFile(documentUploadRule: DocumentUploadRuleResponse[]): PredefinedFile[] {
    const predefinedFiles = [] as PredefinedFile[];

    documentUploadRule.forEach(element => {
      const file = {
        key: String(element.sequence),
        required: element.required ===  YES,
        value: element.description
      } as unknown as PredefinedFile;

      predefinedFiles.push(file);
    });

    return predefinedFiles;
  }

  @OnMount(AccountCreationStepsAliases.FIELD_PASSWORD)
  handleFieldPasswordLoading(event: MountEvent): void {
    const fieldApi = event.target as FieldAPI;
    fieldApi.setHelperText(MessageService.translate('patient.help-message.password', { maxCaracter: CRYPTOPW_MAX_LENGTH }));
  }

  async compareEmails(): Promise<Validation[]> {
    return await this.compareFields(this.email, this.confirmEmail, 'email-match-validator');
  }

  async comparePassword(): Promise<Validation[]> {
    return await this.compareFields(this.password, this.confirmPassword, 'password-match-validator');
  }

  async compareFields(field: string, confirmField: string, validatorName: string): Promise<Validation[]> {

    let validMessage = '';
    let notValidMessage = '';

    if (validatorName === 'email-match-validator') {
      validMessage = 'newaccount.validator.email.confirm.valid';
      notValidMessage = 'newaccount.validator.emails.not.match';
    } else {
      validMessage = 'newaccount.validator.password.confirm.valid';
      notValidMessage = 'newaccount.validator.password.not.match';
    }

    const name = validatorName;
    let message = '';
    let valid;

    if ( field && confirmField ) {
      valid = field === confirmField;
      message = valid ? validMessage : notValidMessage;
    } else {
      valid = true;
    }

    return [{ message, name, valid }];
  }

  addValidators(): void {
    this.fieldConfirmEmail.addLocalValidator( 'email-match-validator', () => { return this.compareEmails() } );
    this.fieldConfirmPassword.addLocalValidator( 'password-match-validator', () => { return this.comparePassword() } );
  }

  @OnMount({ target: 'recaptcha'})
  onMountRecaptcha(event: MountEvent): void {
    const recaptcha = event.target as RecaptchaAPI;
    const { captchFieldApi } = this;
    getCatpchaPublicKey().then(publicKey => {
      if (publicKey && publicKey != null && publicKey.length>0){
        recaptcha.setSiteKey(publicKey);
      } else {
        captchFieldApi.setValidators([]);
        captchFieldApi.setVisible(false);
        captchFieldApi.validate();
      }
    });
  }

  @OnMount({ target: 'wizard-account-creation' })
  handleWizardAccountCreationLoading(event: MountEvent): void {
    this.stepsAccountCreation = event.target as WizardAPI;
  }

  // Removed from step1-personal-identification-controller
  @OnNavigate({ target: 'wizard-account-creation' })
  async onNavigateStepsAccountCreation(evt: AbortableNavigateEvent<string>): Promise<void> {
    const { from, to } = evt;
    const isPreviousStep = WizardUtils.isPreviousStep(this.stepsAccountCreation.getSteps(), to, from);
    if(isPreviousStep) {
      return Promise.resolve();
    }

    let isValidNavigation = false;
    switch (true) {
      case await this.validateStep01(from):
        isValidNavigation = true;
        break;
      case await this.validateStep02(from):
        isValidNavigation = true;
        break;
      case await this.validateStep03(from):
        isValidNavigation = true;
        break;
      default:
        break;
    }

    if(evt.to && isValidNavigation) {
      const destination = evt.to;
      setTimeout(() => {
        this.stepsAccountCreation.setStepDone(destination, true);
      });
      return Promise.resolve();
    }
    evt.abort();
    return Promise.resolve();
  }

  async validateStep01(from: string): Promise<boolean> {
    return from === StepNames.PersonalIdentification && await this.formPersonalIdentificationIsValid();
  }

  async validateStep02(from: string): Promise<boolean> {
    return from === StepNames.PersonalInformation && await this.formPersonalInformationIsValid();
  }

  async validateStep03(from: string): Promise<boolean> {
    return from === StepNames.UploadDocuments && await this.uploadDocumentsIsValid();
  }

  async formPersonalIdentificationIsValid(): Promise<boolean> {
    await this.formPersonalIdentification.validate();
    return this.formPersonalIdentificationFields.filter(field => {
      const validators = field.getValidators() as unknown as [];
      return validators.length > 0;
    }).every(async field => await field.validate());
  }

  async formPersonalInformationIsValid(): Promise<boolean> {
    return await this.formPersonalInformation.validate() && await this.fieldBirthDate.validate(false);
  }

  async formAccountInformationIsValid(): Promise<boolean> {
    return await this.formAccountInformation.validate() && await this.fieldPasswordAPI.validate(false) && await this.fieldUsernameAPI.validate(false);
  }

  async uploadDocumentsIsValid(): Promise<boolean> {
    const requiredFiles = this.fileUploaderDocumentsAPI.getPredefinedFiles()?.filter(e => e.required);
    if (!requiredFiles) {
      return true;
    }

    if (!this.uploadedFiles || this.uploadedFiles.length === 0) {
      return false;
    }

    const uploadedFiles = [];
    requiredFiles.forEach(itemRule => {
      const file = this.uploadedFiles.find(e => e.predefinedFileKey === itemRule.key);
      if (file) {
        uploadedFiles.push(file);
      }
    });

    await this.fieldUploaderDocumentsAPI.validate();
    return uploadedFiles.length > 0;
  }

  // Removed from step1-personal-identification-controller
  isEmpty(field: FieldAPI): boolean {
    return field.getValue() == null || String(field.getValue()).trim() === '';
  }

  // Removed from step1-personal-identification-controller
  @OnMount({ target: [
    'field-brasil-cpf',
    'field-brasil-identidade',
    'field-foreign-passport',
    'field-foreign-id',
    'field-foreign-nationality',
    'field-phone',
    'field-use-foreing-ids',
    'field-verifying-digit',
    'field-medicare',
    'field-document-type',
    'field-document-value',
    'fetch-document-type'] })
  handleFormPersonalIdentificationFieldsOnEditMode(evt: MountEvent): void {
    const fieldApi = evt.target as FieldAPI;
    if (fieldApi.getAlias() === 'field-verifying-digit') {
      this.addAlphaNumericValidationBolivia(fieldApi);
    }

    if (this.language === LocaleTypes.AUSTRALIA) {
      this.fieldForeignId.removeValidator('required');
      this.fieldForeignPassport.removeValidator('required');
      this.fieldForeignNationality.removeValidator('required');
    }

    this.saveCopyOfValidations(evt);
  }

  // Removed from step1-personal-identification-controller
  saveCopyOfValidations(evt: MountEvent): void {
    const target: FieldAPI = cast(evt.target);
    this.backupOfValidatorsStep01[target.getEntry()] = clone(target.getValidators());
    this.formPersonalIdentificationFields.push(target);
  }

  // Removed from step1-personal-identification-controller
  @OnMount({ target: 'form-view-personal-identification' })
  onMountFormViewPersonalIdentification(evt: MouseEvent): void {
    const form: FormAPI = cast(evt.target);
    form.setValue(this.formPersonalIdentification.getValue());

    this.reArrangePersonalIdentificationForView(this.useForeignIds);
  }

  @OnMount({ target: 'form-view-personal-information' })
  onMountFormViewPersonalInformation(evt: MouseEvent): void {
    const form: FormAPI = evt.target as unknown as FormAPI;
    if ('key' === this.context.get('nestedPropertyGender')) {
      this.fieldViewGenderAPI.setEntry('personGender.value');
    }
    form.setValue(this.formPersonalInformation.getValue());
    this.reArrangePersonalInformationForView();
  }

  // Removed from step1-personal-identification-controller
  private reArrangePersonalIdentificationForView(useForeignIds: boolean): void {
    if (useForeignIds) {
      this.setViewVisibilityForForeigner();
    } else {
      this.setViewVisibilityForNotForeigner();
    }
  }

  // Removed from step1-personal-identification-controller
  private async setViewVisibilityForNotForeigner(): Promise<void> {
    if (this.language === LocaleTypes.AUSTRALIA) {
      this.setVisibility(await this.fieldIsValidAndNotEmpty(this.fieldMedicare), this.fieldViewMedicare);

      this.setVisibility(false, this.fieldViewBrasilIdentidade);
      this.setVisibility(false, this.fieldViewBrasilCpf);
    } else if (this.language === LocaleTypes.BOLIVIA) {
      const isDepartSelect = this.fieldDepartments.getValue() !== undefined;
      this.setVisibility(isDepartSelect, this.fieldViewDepartments);
      this.setVisibility(await this.fieldIsValidAndNotEmpty(this.fieldBrasilIdentidade), this.fieldViewBrasilIdentidade);
      this.setVisibility(await this.fieldIsValidAndNotEmpty(this.fieldVerifyingDigit), this.fieldViewVerifyingDigit);

      this.setVisibility(false, this.fieldViewBrasilCpf);
    } else if (this.language === LocaleTypes.ARGENTINA) {
      this.setVisibility(await this.fieldIsValidAndNotEmpty(this.fieldBrasilCpf), this.fieldViewBrasilCpf);
      this.setVisibility(await this.fieldIsValidAndNotEmpty(this.fieldBrasilIdentidade), this.fieldViewBrasilIdentidade);
    } else if (this.language === LocaleTypes.COLOMBIA) {
      this.setVisibility(true, this.fieldDocumentType, this.fieldDocumentValue);
      this.setVisibility(false, this.fieldViewBrasilIdentidade, this.fieldViewBrasilCpf);
    } else {
      this.setVisibility(await this.fieldIsValidAndNotEmpty(this.fieldBrasilCpf), this.fieldViewBrasilCpf);
      this.setVisibility(await this.fieldIsValidAndNotEmpty(this.fieldBrasilIdentidade), this.fieldViewBrasilIdentidade);
      if (this.language === LocaleTypes.MEXICO && await this.fieldIsValidAndNotEmpty(this.fieldBrasilIdentidade)){
        this.fieldViewBrasilIdentidade.setLabel(MessageService.translate('tws.label.id.rfc'))
      }
    }

    this.setVisibility(false, this.fieldViewForeignNationality, this.fieldViewForeignId, this.fieldViewForeignPassport);
  }

  // Removed from step1-personal-identification-controller
  private async fieldIsValidAndNotEmpty(field: FieldAPI): Promise<boolean> {
    return await field.validate() && typeof field.getValue() === 'string' && String(field.getValue()).trim() !== '';
  }

  // Removed from step1-personal-identification-controller
  private async setViewVisibilityForForeigner(): Promise<void> {
    this.setVisibility(true, this.fieldViewForeignNationality);
    this.setVisibility(await this.fieldIsValidAndNotEmpty(this.fieldForeignId), this.fieldViewForeignId);
    this.setVisibility(await this.fieldIsValidAndNotEmpty(this.fieldForeignPassport), this.fieldViewForeignPassport);

    if (this.language  === LocaleTypes.AUSTRALIA) {
      this.setVisibility(false, this.fieldViewBrasilCpf, this.fieldViewMedicare, this.fieldViewBrasilIdentidade);
    } else if (this.language  === LocaleTypes.BOLIVIA) {
      this.setVisibility(false, this.fieldViewBrasilCpf, this.fieldViewBrasilIdentidade, this.fieldViewVerifyingDigit, this.fieldViewDepartments);
    } else if (this.language  === LocaleTypes.COLOMBIA) {
      this.setVisibility(false, this.fieldViewBrasilCpf, this.fieldViewBrasilIdentidade, this.fieldViewVerifyingDigit, this.fieldViewDepartments);
    } else {
      this.setVisibility(false, this.fieldViewBrasilCpf, this.fieldViewBrasilIdentidade);
    }
  }

  private reArrangePersonalInformationForView(): void {
    switch (this.language) {
      case LocaleTypes.COLOMBIA:
        this.setVisibility(true, this.fieldViewMainPersonName1Api, this.fieldViewMainPersonName2Api, this.fieldViewMainPersonName3Api, this.fieldViewMainPersonName4Api);
        this.setVisibility(false, this.fieldViewFullnameApi);
        this.setProperties(this.fieldViewMainPersonName1Api, MessageService.translate('patient.profile.main.given-name'), 'personMainGivenName', true);
        this.setProperties(this.fieldViewMainPersonName2Api, MessageService.translate('patient.profile.main.component-name-1'), 'personMainComponentName1', false);
        this.setProperties(this.fieldViewMainPersonName3Api, MessageService.translate('patient.profile.locale.colombia.family-name'), 'personMainFamilyName', true);
        this.setProperties(this.fieldViewMainPersonName4Api, MessageService.translate('patient.profile.locale.colombia.component-name-2'), 'personMainComponentName2', false);
        break;
      case LocaleTypes.MEXICO:
        this.setVisibility(true, this.fieldViewMainPersonName1Api, this.fieldViewMainPersonName2Api, this.fieldViewMainPersonName3Api);
        this.setVisibility(false, this.fieldViewFullnameApi);
        this.setProperties(this.fieldViewMainPersonName1Api, MessageService.translate('patient.profile-name'), 'personMainGivenName', true);
        this.setProperties(this.fieldViewMainPersonName2Api, MessageService.translate('patient.profile.locale.colombia.family-name'), 'personMainFamilyName', true);
        this.setProperties(this.fieldViewMainPersonName3Api, MessageService.translate('patient.profile.main.component-name-3'), 'personMainComponentName1', false);
        break;
      default:
        this.setVisibility(false, this.fieldViewMainPersonName1Api, this.fieldViewMainPersonName2Api, this.fieldViewMainPersonName3Api, this.fieldViewMainPersonName4Api);
        this.setVisibility(true, this.fieldViewFullnameApi);
        break;
    }
  }

  @OnMount({ target: ['form-personal-identification', 'form-personal-information', 'form-account-information'] })
  onMountAllForms(evt: MountEvent): void {
    const form = evt.target as FormAPI;
    switch (form.getAlias()) {
      case 'form-personal-identification':
        this.onMountModalAccountCreation();
        break;
      default:
        break;
    }

    this.allForms.push(cast(evt.target));
    this.addValidators();
  }

  // Removed from step1-personal-identification-controller
  onMountModalAccountCreation(): void {
    this.useForeignIds = false;
    this.reArrangePersonalIdentification(this.useForeignIds);
    this.updateDefaultPhoneDdi();
    this.setIfIdNumberWillBeShownInCheckbox();
    this.reArrangePersonalInformation();
  }

  // Removed from step1-personal-identification-controller
  private reArrangePersonalIdentification(useForeignIds: boolean): void {
    // Enable or disable fields based on useForeignIds value.
    switch (this.language) {
      case LocaleTypes.AUSTRALIA:
        this.setAustraliaReArrangePersonalIdentification(useForeignIds)
        break;
      case LocaleTypes.BOLIVIA:
        this.setBoliviaReArrangePersonalIdentification(useForeignIds)
        this.fieldVerifyingDigit.removeValidator('required');
        break;
      case LocaleTypes.ARGENTINA:
        this.setArgentinaReArrangePersonalIdentification(useForeignIds);
        break;
      case LocaleTypes.MEXICO:
        this.setMexicoReArrangePersonalIdentification(useForeignIds)
        break;
      case LocaleTypes.COLOMBIA:
        this.setColombiaReArrangePersonalIdentification(useForeignIds)
        break;
      default:
        this.setVisibility(!useForeignIds, this.fieldBrasilCpf, this.fieldBrasilIdentidade);
        this.setVisibility(useForeignIds, this.fieldForeignPassport, this.fieldForeignId, this.fieldForeignNationality);

        this.setValidatorsEnable(false, this.fieldVerifyingDigit);
        this.setValidatorsEnable(!useForeignIds, this.fieldBrasilCpf, this.fieldBrasilIdentidade);
        this.setValidatorsEnable(useForeignIds, this.fieldForeignPassport, this.fieldForeignId, this.fieldForeignNationality);
        this.setVisibility(false, this.fieldDocumentType, this.fieldDocumentValue);
        break;
    }
  }

  private setMexicoReArrangePersonalIdentification(useForeignIds: boolean): void {
    this.setVisibility(!useForeignIds, this.fieldBrasilCpf, this.fieldBrasilIdentidade);
    this.setVisibility(useForeignIds, this.fieldForeignPassport, this.fieldForeignId, this.fieldForeignNationality);
    this.setValidatorsEnable(useForeignIds, this.fieldForeignPassport, this.fieldForeignId, this.fieldForeignNationality);
    this.setValidatorsEnable(false, this.fieldBrasilCpf, this.fieldBrasilIdentidade, this.fieldVerifyingDigit);
    const required = {
      'name': 'required',
      'options': {}
    }
    if(!useForeignIds){
      const curpValidator = {
        'name': 'max-length',
        'options': {
          'max': 18
        }
      }
      const rfcValidator = {
        'name': 'max-length',
        'options': {
          'max': 13
        }
      }
      this.fieldBrasilIdentidade.setLabel(MessageService.translate('tws.label.id.rfc'))
      this.fieldBrasilCpf.addValidator(curpValidator)
      this.fieldBrasilCpf.addValidator(required)
      this.fieldBrasilIdentidade.addValidator(rfcValidator)
    } else {
      this.setValidatorsEnable(false, this.fieldForeignId);
      this.fieldForeignId.addValidator(required)
    }
  }
  // Removed from step1-personal-identification-controller
  private setAustraliaReArrangePersonalIdentification(useForeignIds: boolean): void {
    this.setVisibility(false, this.fieldBrasilCpf, this.fieldBrasilIdentidade);
    this.setVisibility(!useForeignIds, this.fieldMedicare);
    this.setVisibility(useForeignIds, this.fieldForeignPassport, this.fieldForeignId, this.fieldForeignNationality);

    this.setValidatorsEnable(false, this.fieldVerifyingDigit, this.fieldBrasilCpf, this.fieldBrasilIdentidade);
    this.setValidatorsEnable(!useForeignIds, this.fieldMedicare);
  }

  // Removed from step1-personal-identification-controller
  private setBoliviaReArrangePersonalIdentification(useForeignIds: boolean): void {
    this.setVisibility(false, this.fieldBrasilCpf);
    this.setVisibility(!useForeignIds, this.fieldBrasilIdentidade, this.fieldVerifyingDigit, this.fieldDepartments);
    this.setVisibility(useForeignIds, this.fieldForeignPassport, this.fieldForeignId, this.fieldForeignNationality);

    this.setValidatorsEnable(false, this.fieldBrasilCpf);
    this.setValidatorsEnable(!useForeignIds, this.fieldBrasilIdentidade, this.fieldVerifyingDigit, this.fieldDepartments);
  }

  private setArgentinaReArrangePersonalIdentification(useForeignIds: boolean): void {
    this.setVisibility(!useForeignIds, this.fieldBrasilCpf, this.fieldBrasilIdentidade);
    this.setVisibility(useForeignIds, this.fieldForeignPassport, this.fieldForeignId, this.fieldForeignNationality);

    const dniValidator = PredefinedValidators.argentinaDNI();
    const requiredValidator = PredefinedValidators.required();
    this.fieldBrasilCpf.setValidators([dniValidator, requiredValidator]);

    const cuilValidator = PredefinedValidators.argentinaCUIL();
    this.fieldBrasilIdentidade.setValidators([cuilValidator]);
    this.fieldBrasilIdentidade.setLabel('patient.label.secondary-document');
  }

  private setColombiaReArrangePersonalIdentification(useForeignIds: boolean): void {
    this.setVisibility(false, this.fieldBrasilCpf, this.fieldBrasilIdentidade);
    this.setVisibility(useForeignIds, this.fieldForeignPassport, this.fieldForeignId, this.fieldForeignNationality);

    this.setValidatorsEnable(false, this.fieldBrasilCpf, this.fieldBrasilIdentidade, this.fieldVerifyingDigit);
    this.setVisibility(!useForeignIds, this.fieldDocumentType, this.fieldDocumentValue);
    this.fieldDocumentValue.setLabel(MessageService.translate('account.document.label.civilid'));
  }

  // Removed from step1-personal-identification-controller
  setVisibility(visible: boolean, ...linkedFields: FieldAPI[]): void {
    linkedFields.forEach(e => e.setVisible(visible));
  }

  // Removed from step1-personal-identification-controller
  setValidatorsEnable(enable: boolean, ...linkedFields: FieldAPI[]): void {
    linkedFields.forEach(e => e.setValidators(enable ? cast(this.backupOfValidatorsStep01[e.getEntry()]) as unknown as Validator[] : [] as unknown as Validator[]));
  }

  setProperties(linkedField: FieldAPI, label: string, entry: string, mandatory: boolean): void {
    linkedField.setLabel(label);
    linkedField.setEntry(entry);
    if(mandatory) {
      const required = {
        'name': 'required',
        'options': {}
      }
      linkedField.addValidator(required);
    }
  }

  // Removed from step1-personal-identification-controller
  private updateDefaultPhoneDdi(): void {
    const phoneCountry = this.language.split('-')[1].toLocaleUpperCase()
    this.inputPhone.setCountry(phoneCountry as Country)
  }

  // Removed from step1-personal-identification-controller
  private setIfIdNumberWillBeShownInCheckbox(): void {
    HttpService.get<LoginTypeParamResponse>(APPLICATION_ENDPOINTS.GET_LOGIN_TYPE_PARAM, {functionCode: 9111, functionParameter: 13})
      .then(r => {
        if (r.body?.loginTypeParam === 'S') {
          this.checkboxUseForeingIds.setLabel('newaccount.field.donthave.cpf');
        }
      })
  }

  private reArrangePersonalInformation(): void {
    switch (this.language) {
      case LocaleTypes.COLOMBIA:
        this.setVisibility(true, this.fieldMainPersonName1Api, this.fieldMainPersonName2Api, this.fieldMainPersonName3Api, this.fieldMainPersonName4Api);
        this.setVisibility(false, this.fieldFullname);
        this.setProperties(this.fieldMainPersonName1Api, MessageService.translate('patient.profile.main.given-name'), 'personMainGivenName', true);
        this.setProperties(this.fieldMainPersonName2Api, MessageService.translate('patient.profile.main.component-name-1'), 'personMainComponentName1', false);
        this.setProperties(this.fieldMainPersonName3Api, MessageService.translate('patient.profile.locale.colombia.family-name'), 'personMainFamilyName', true);
        this.setProperties(this.fieldMainPersonName4Api, MessageService.translate('patient.profile.locale.colombia.component-name-2'), 'personMainComponentName2', false);
        break;
      case LocaleTypes.MEXICO:
        this.setVisibility(true, this.fieldMainPersonName1Api, this.fieldMainPersonName2Api, this.fieldMainPersonName3Api);
        this.setVisibility(false, this.fieldFullname);
        this.setProperties(this.fieldMainPersonName1Api, MessageService.translate('patient.profile-name'), 'personMainGivenName', true);
        this.setProperties(this.fieldMainPersonName2Api, MessageService.translate('patient.profile.locale.colombia.family-name'), 'personMainFamilyName', true);
        this.setProperties(this.fieldMainPersonName3Api, MessageService.translate('patient.profile.main.component-name-3'), 'personMainComponentName1', false);
        break;
      default:
        this.setVisibility(false, this.fieldMainPersonName1Api, this.fieldMainPersonName2Api, this.fieldMainPersonName3Api, this.fieldMainPersonName4Api);
        this.setVisibility(true, this.fieldFullname);
        break;
    }
  }

  // Removed from step1-personal-identification-controller
  @OnChange({ target: 'checklist-use-foreing-ids' })
  onChangeChecklistUseForeingIds(evt: ChangeEvent<boolean>): void {
    this.useForeignIds = evt.value;
    this.reArrangePersonalIdentification(this.useForeignIds);
  }

  @OnSubmit({ target: AccountCreationStepsAliases.WIZARD_ACCOUNT_CREATION })
  async onSubmitStepsAccountCreation(event: ClearEvent<string>): Promise<void> {
    await this.onClearPersonalInformation(event);
    await this.onClearPersonalIdentification(event);
    const recoveryTypeProps = this.fieldValidationType.getValue() as RecoveryTypeProps;
    const recoveryDocumentType = this.fieldDocumentType.getValue() as DocumentType;

    if(!await this.formAccountInformationIsValid()) {
      return;
    }

    const body: Record<string, Value> = { ...this.readAllFormsAndReturnBody(), documentType: recoveryDocumentType?.documentName, validationType: recoveryTypeProps?.value || 'E' };

    const listOfError: ValidationErrors[] = cast(await HttpService.put(APPLICATION_ENDPOINTS.ACCOUNT_CREATION_VALIDATOR, { fields: ['accountEmail'] }, { body }).then(r => r.body));

    if (listOfError.length > 0) {
      return;
    }

    let memoryFiles: Map<string, Blob> = new Map();
    if (!this.isStepHidden(StepNames.UploadDocuments)) {
      const memoryFileHandler = this.fileUploaderDocumentsAPI.getFileHandlerInstance() as MemoryFileHandler;
      memoryFiles = memoryFileHandler.getMemoryFiles();
    }

    ModalService.openDialog({
      closeable: false,
      contextData: { body, infoUploadedFiles: this.uploadedFiles, memoryFiles},
      fragment: 'account-creation-processing',
      theme: ThemeTonalRange.ULTRA_LIGHT,
    });
  }

  // Removed from step1-personal-identification-controller
  async onClearPersonalIdentification(evt: ClearEvent<string>): Promise<void> {
    if (evt.entry !== StepNames.PersonalIdentification) {
      return;
    }

    const useForeignIds = this.formPersonalIdentification.getValue().useForeignIds

    if (useForeignIds) {
      this.formPersonalIdentification.setValue({
        foreignNationality: {},
        useForeignIds,
      });
    } else {
      if (this.language === LocaleTypes.BOLIVIA) {
        this.formPersonalIdentification.setValue({ sgDepartamento: {} });
      } else {
        this.formPersonalIdentification.setValue({});
      }
    }

    await this.formPersonalIdentification.validate();
  }

  // The same logic:
  // Removed from step3-personal-identification-controller
  // Removed from step2-personal-identification-controller
  // Old name in step2 and step3: onClearModalAccountCreation
  async onClearPersonalInformation(evt: ClearEvent<string>): Promise<void> {
    switch (evt.entry) {
      case StepNames.AccountInformation:
        break;
      case StepNames.PersonalInformation:
        break;
      default:
        return;
    }

    this.formPersonalInformation.setValue({});
    await this.formPersonalInformation.validate();
  }

  readAllFormsAndReturnBody(): Record<string, Value> {
    return this.allForms.map(form => form.getRecord())
      .map((record: Record<string, Value>) => {
        this.replaceValueWithNestedProperty(record, 'foreignNationality', 'cdNacionalidade');
        this.replaceValueWithNestedProperty(record, 'personGender', cast(this.context.get('nestedPropertyGender')));
        this.replaceValueWithNestedProperty(record, 'sgDepartamento', 'vlDominio');

        return record;
      })
      .reduce((prev, curr) => Object.assign(prev, curr), {});
  }

  private replaceValueWithNestedProperty(object: Record<string, Value>, propertyName: string, nestedProperty: string): void {
    Object.keys(object)
      .filter(key => key === propertyName)
      .forEach(key => {
        const nestedObject: Record<string, Value> = cast(object[key]);
        object[key] = nestedObject[nestedProperty];
      });
  }

  @OnClickTermo('checkbox-agree-terms')
  onClickTermo(): void {
    PatientModalService.openDialog( {
      closeable: true,
      closeByDocument: false,
      closeByEscape: false,
      contextData: { termType: TermsOfUseType.TERM_OF_USE },
      focusOnOpen: false,
      fragment: 'account-creation-terms-modal',
      onClose: () => { return; },
      theme: ThemeTonalRange.ULTRA_LIGHT,
      title: 'patient.terms.use',
      uuid: generateUUID(),
    });
  }

  @OnClickTermoConsent('checkbox-agree-terms')
  onClickTermoConsent(): void {
    PatientModalService.openDialog({
      closeable: true,
      closeByDocument: false,
      closeByEscape: false,
      contextData: { termType: TermsOfUseType.TERM_OF_CONSENT },
      focusOnOpen: false,
      fragment: 'account-creation-terms-modal',
      onClose: () => { return; },
      theme: ThemeTonalRange.ULTRA_LIGHT,
      title: 'patient.terms.consent',
      uuid: generateUUID(),
    });
  }

  @OnClickAviso('checkbox-agree-terms')
  onClickAviso(): void {
    PatientModalService.openDialog({
      closeable: true,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'account-creation-privacy-modal',
      onClose: () => { return; },
      theme: ThemeTonalRange.ULTRA_LIGHT,
      title: 'newaccount.link.privacy.notice',
      uuid: generateUUID(),
    });
  }

  @OnClear({ target: 'wizard-account-creation' })
  onWizardAccountInformationClear(event: ClearEvent<string>): void {
    switch(event.entry) {
      case StepNames.AccountInformation: {
        this.fieldUserInactive.setVisible(false);
        if(this.fieldConfirmEmail.isVisible()) {
          this.fieldConfirmEmail.removeLocalValidator('email-match-validator');
          this.fieldConfirmEmail.removeValidator('required');
          this.fieldConfirmPassword.removeLocalValidator( 'password-match-validator');
        } else {
          this.groupFieldsVisibility(true);
        }
        const formRecord = this.formAccountInformation.getRecord();
        this.formAccountInformation.setRecord({});
        this.formAccountInformation.setData({accountCaptcha: formRecord.accountCaptcha});
        break;
      }
      case StepNames.PersonalIdentification: {
        const formRecord = this.formPersonalIdentification.getRecord();
        const useForeignIds = formRecord.useForeignIds as boolean;
        this.formPersonalIdentification.setRecord({});
        this.formPersonalIdentification.setData({
          foreignNationality: {},
          useForeignIds,
        });
        break;
      }
      case StepNames.PersonalInformation: {
        this.formPersonalInformation.setRecord({});
        this.formPersonalInformation.setData({personGender: {}});
        break;
      }
    }
  }

  @OnChange({ target: AccountCreationStepsAliases.FIELD_EMAIL })
  async onChangeEmail(event: ChangeEvent<string>): Promise<void> {
    this.email = event.value;
    this.emailField.removeValidator('emailAlreadyExistsValidator');
    this.handleEmailChangeTimeout();
    const confirmEmail = this.inputConfirmEmail?.getValue();
    if (confirmEmail) {
      await this.fieldConfirmEmail.validate();
    }
  }

  @OnChange({ target: AccountCreationStepsAliases.INPUT_CONFIRM_EMAIL })
  onChangeConfirmEmail(event: ChangeEvent<string>): void {
    this.confirmEmail = event.value;
  }

  @OnChange({ target: AccountCreationStepsAliases.INPUT_PASSWORD })
  async onPasswordChange( event: ChangeEvent<string> ): Promise<void> {
    this.password = event.value;

    const confirmPassword = this.inputConfirmPassword.getValue();

    if ( confirmPassword ) {
      await this.fieldConfirmPassword.validate();
    }
  }

  @OnChange({ target: AccountCreationStepsAliases.INPUT_CONFIRM_PASSWORD })
  onConfirmPasswordChange( event: ChangeEvent<string> ): void {
    this.confirmPassword = event.value;
  }

  @OnClick({ target: AccountCreationStepsAliases.LINK_USER_INACTIVE })
  onClickLinkUserInactive(): void {
    const email = this.formAccountInformation.getRecord().accountEmail;

    HttpService.post(APPLICATION_ENDPOINTS.RESEND_ACTIVATION_MAIL, { email })
      .then(() => {
        this.historyService.push(APPLICATION_ROUTES.ACCOUNT_CREATION_COMPLETED);
      });
  }

  @OnMount({target: 'field-dob'})
  handleLoadBirthDateField(event: MountEvent): void {
    const field = event.target as FieldAPI;
    ConfigurationService.getConfigurationData().then(response => {
      field.addValidator({name: 'validate-birthday-by-tasy-param', options:{minAge: response.minAgeToRegister}})
    });
  }

  @OnChange([AccountCreationStepsAliases.FORM_PERSONAL_IDENTIFICATION])
  handleEditPersonalIdentificationForm(): void {
    this.stepsAccountCreation.setStepDone(StepNames.PersonalIdentification, true);
  }

  @OnChange([AccountCreationStepsAliases.FORM_PERSONAL_INFORMATION])
  handleEditPersonalInformationForm(): void {
    this.stepsAccountCreation.setStepDone(StepNames.PersonalInformation, true);
  }

  @OnChange([AccountCreationStepsAliases.FORM_ACCOUNT_INFORMATION])
  handleEditAccountInformationForm(): void {
    this.stepsAccountCreation.setStepDone(StepNames.AccountInformation, true);
  }

  @OnChange([AccountCreationStepsAliases.AUTOCOMPLETE_DOCUMENT_TYPE])
  handleInputAccountDocumentType( event: ChangeEvent<DocumentType> ):void{
    const documentType = event.value as DocumentType;
    this.fieldDocumentValue.setLabel(DocumentTypeDefinitions.getLabelExpression(documentType));
    this.inputDocumentValue.setValue('');
    this.inputDocumentValue.setLength(DocumentTypeDefinitions.getMaxLength(documentType));
  }

  @OnBlur([AccountCreationStepsAliases.FIELD_BRASIL_CPF, AccountCreationStepsAliases.FIELD_BRASIL_IDENTIDADE])
  handleOutFocusPersonIdsField(event: FocusEvent): void {
    const fieldAPI = event.target as FieldAPI;
    this.handleOutFocusPersonIdsFieldForArgentina(fieldAPI);
  }

  /**
   * This method will remove or add one of the validators (argentinaDNI or argentinaCUIL)
   * if the current field has some value and will also apply the mandatory.
   */
  private handleOutFocusPersonIdsFieldForArgentina(fieldApi: FieldAPI): void {
    const currentLanguage = LanguageService.getCurrentLanguage();
    if (currentLanguage !== LocaleTypes.ARGENTINA) {
      return;
    }

    let dependentField: FieldAPI;
    switch (fieldApi.getAlias()) {
      case AccountCreationStepsAliases.FIELD_BRASIL_CPF:
        dependentField = this.fieldBrasilIdentidade;
        break;
      case AccountCreationStepsAliases.FIELD_BRASIL_IDENTIDADE:
        dependentField = this.fieldBrasilCpf;
        break;
      default:
        return;
    }

    AccountCreationArgentinaHelper.handlePersonIdsFieldsWhenOutFocus(fieldApi, dependentField);
  }

  @OnBlur([AccountCreationStepsAliases.FIELD_FOREIGN_ID, AccountCreationStepsAliases.FIELD_FOREIGN_PASSPORT])
  handleOutFocusForeignFields(event: FocusEvent): void {
    const fieldAPI = event.target as FieldAPI;
    this.handleOutFocusForeignFieldsForBolivia(fieldAPI);
  }

  handleOutFocusForeignFieldsForBolivia(fieldApi: FieldAPI): void {
    const currentLanguage = LanguageService.getCurrentLanguage();
    if (currentLanguage !== LocaleTypes.BOLIVIA) {
      return;
    }

    AccountCreationBoliviaHelper.handleForeignFieldsOutFocus(fieldApi, this.fieldForeignId, this.fieldForeignPassport);
  }

  @OnMount('input-verifying-digit')
  handleInputVerifyingDigitLoading(event: MountEvent): void {
    const input = event.target as InputAPI;
    this.updateInputTypeForVerifyingDigit(input);
  }

  updateInputTypeForVerifyingDigit(input: InputAPI): void {
    const currentLanguage = LanguageService.getCurrentLanguage();
    if (currentLanguage === LocaleTypes.BOLIVIA) {
      input.setType(InputType.TEXT);
    }
  }

  addAlphaNumericValidationBolivia(fieldApi: FieldAPI): void {
    const currentLanguage = LanguageService.getCurrentLanguage();
    if (currentLanguage === LocaleTypes.BOLIVIA) {
      fieldApi.addValidator({ name: 'alphaNumericNew' });
    }
  }

  @OnUnmount({ target: AccountCreationStepsAliases.LAYOUT_ROOT })
  handleCloseFragement(): void {
    ToastService.closeAll();
  }

  @OnMount({ target: AccountCreationStepsAliases.FIELD_SMS_EMAIL })
  async getSettingInformations(): Promise<void> {
    const response = await HttpService.get(APPLICATION_ENDPOINTS.ACCOUNT_SETTINGS);
    const accountCreationSettings = response.body as AccountCreationSettings;

    if(!accountCreationSettings || accountCreationSettings.ieSmsActivation !== 'S' ){
      this.fieldValidationType.setVisible(false);
      this.fieldValidationType.removeValidator('required');
      await this.fieldValidationType.validate();
      this.addEmailAndConfirmEmailValidators(false);
    }
  }

  @OnMount({ target: AccountCreationStepsAliases.RADIO_SMS_EMAIL })
  onMountRadioGroup( evt: MountEvent ): void {
    const radioSmsEmail = evt.target as RadioGroupAPI;
    radioSmsEmail.setData([{key:'S',label: MessageService.translate('patient.sms'), value:'S'},{key:'E',label:MessageService.translate('patient.email'), value:'E'}]);
  }

  @OnChange({ target: AccountCreationStepsAliases.RADIO_SMS_EMAIL })
  onChooseValidationTypeChange( event: ChangeEvent<Record<string, Value>> ): void {

    const recoveryType = event.value as unknown as RecoveryTypeProps;

    if(recoveryType.value === 'S'){
      this.fieldConfirmEmail.removeLocalValidator('email-match-validator');
      this.fieldConfirmEmail.removeValidator('required');
      this.removeRalidators([this.emailField, this.fieldConfirmEmail], ['required']);
    }else{
      this.addEmailAndConfirmEmailValidators();
    }
  }

  private addEmailAndConfirmEmailValidators(showValidations?: boolean): void {
    this.pushValidators([this.emailField], ['emailValidator', 'required'], showValidations);
    this.pushValidators([this.fieldConfirmEmail], ['required'], showValidations);
    this.fieldConfirmEmail.addLocalValidator( 'email-match-validator', async () => { return await this.compareEmails() } );
  }

  removeRalidators(fields: FieldAPI[], validators: string[], showValidations?: boolean):void{
    fields.forEach(field=> {
      validators.forEach(validator => field.removeValidator(validator));
      field.validate(showValidations);
    });
  }

  pushValidators(fields: FieldAPI[], validators: string[], showValidations?: boolean):void{
    fields.forEach(field=> {
      field.setValidators([]);
      validators.forEach(validator => field.addValidator({ name: validator }));
      field.validate(showValidations);
    });
  }

  @OnMount(AccountCreationStepsAliases.FIELD_USERNAME)
  handleFieldUsernameLoading(event: MountEvent): void {
    const usernameFieldAPI = event.target as FieldAPI;
    usernameFieldAPI.setHelperText(MessageService.translate('patient.input.min-length', { minCaracter: USERNAME_MIN_LENGTH_VALIDATION }));
  }

  @OnBlur({ target: AccountCreationStepsAliases.FIELD_USERNAME })
  handleFieldUsernameFocusOut(event: FocusEvent): void {
    const usernameFieldAPI = event.target as FieldAPI;
    UsernameValidatorUtils.validateUsernameField(usernameFieldAPI);
    setTimeout(() => usernameFieldAPI.validate());
  }

  @OnBlur([AccountCreationStepsAliases.FIELD_EMAIL, AccountCreationStepsAliases.FIELD_CONFIRM_EMAIL])
    handleFieldUsernameOnFocus = (): void => {
      const isValidEmail = this.emailField.isVisible() && this.emailField.validate(false);
      const isValidConfirmationEmail = this.fieldConfirmEmail.isVisible() && this.fieldConfirmEmail.validate(false);

      if (isValidEmail && isValidConfirmationEmail && !this.inputUsernameAPI.getValue()) {
        const userEmail = this.emailField.getValue() as string;
        const usernameSuggestion = userEmail.substring(0, userEmail.indexOf('@'));
        this.inputUsernameAPI.setPlaceholder(`${MessageService.translate('tws.appointment-suggestion.suggestion')}: ${usernameSuggestion}`);
      }
      this.validateEmail();
    }

  @OnAfterFetch({ target: AccountCreationStepsAliases.FETCH_GENDER})
  handleGenderFields(): void {
    this.context.set({ nestedPropertyGender: 'vlDominio' });
  }

  private isStepHidden(stepName: string): string | undefined {
    const steps = this.stepsAccountCreation.getHiddenSteps();
    return steps.find(step => step === stepName);
  }

  private handleEmailChangeTimeout(): void {
    if (this.emailChangeTimeout) {
      clearTimeout(this.emailChangeTimeout);
    }

    this.emailChangeTimeout = setTimeout(() => {
      this.validateEmail();
    }, DEFAULT_TIMEOUT.SHORT);
  }

  private validateEmail(): void {
    const email = this.formAccountInformation.getRecord().accountEmail as string;
    if (!email || !email.includes('@') || !email.includes('.')) {
      this.groupFieldsVisibility(true);
      this.fieldUserInactive.setVisible(false);
      this.emailField.validate();
      return;
    }
    PatientHttpService.get(APPLICATION_ENDPOINTS.IS_EMAIL_VALID, { email }).then(() => {
      this.fieldUserInactive.setVisible(false);
      this.groupFieldsVisibility(true);
      this.emailField.validate();
    }).catch((e: HttpServiceError<ValidationErrors>) => {
      const error = e.response?.body;
      this.groupFieldsVisibility(false);
      if (error?.messageKey === 'newaccount.backend.error.email.ispending.toactivate') {
        this.fieldUserInactive.setVisible(true);
      } else {
        this.fieldUserInactive.setVisible(false);
        if (!this.emailField.getValidators().find(element => element.name === 'emailAlreadyExistsValidator')) {
          this.emailField.addValidator({ name: 'emailAlreadyExistsValidator' });
        }
      }
      this.emailField.validate();
    });
  }

  private groupFieldsVisibility(option: boolean): void {
    if (option) {
      this.showAccountFields();
    } else {
      this.hideAccountFields();
    }
  }

  private showAccountFields(): void {
    this.fieldConfirmEmail.setVisible(true);
    this.fieldUsernameAPI.setVisible(true);
    this.fieldPasswordAPI.setVisible(true);
    this.fieldConfirmPassword.setVisible(true);
    this.fieldAgreeTermsAPI.setVisible(true);
    this.captchFieldApi.setVisible(true);
  }

  private hideAccountFields(): void {
    this.fieldConfirmEmail.setVisible(false);
    this.fieldUsernameAPI.setVisible(false);
    this.fieldPasswordAPI.setVisible(false);
    this.fieldConfirmPassword.setVisible(false);
    this.fieldAgreeTermsAPI.setVisible(false);
    this.captchFieldApi.setVisible(false);
  }

}
